import { useRuntimeConfig } from "nuxt/app";
import queryString from "query-string";
import { DateTime } from "luxon";
export function useCrudConverters() {
  const convertAddress = (item, separator = ", ") => {
    let components = [];
    if (item.addressLine1) components.push(item.addressLine1);
    if (item.addressLine2) components.push(item.addressLine2);
    if (item.city) components.push(item.city);
    if (item.postCode) components.push(item.postCode);
    if (item.country) components.push(item.country.name);
    return components.join(separator);
  };
  const flattenQuery = (obj, prefix = "") => {
    if (obj === null || obj === void 0) {
      return {};
    }
    return Object.keys(obj).reduce((acc, key) => {
      const newKey = prefix ? `${prefix}[${key}]` : key;
      if (typeof obj[key] === "object" && !Array.isArray(obj[key]) && obj[key] !== null) {
        Object.assign(acc, flattenQuery(obj[key], newKey));
      } else {
        acc[newKey] = obj[key];
      }
      return acc;
    }, {});
  };
  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  const mimeTypeToMdiIcon = (mimeType) => {
    switch (mimeType) {
      case "image/jpg":
      case "image/jpeg":
      case "image/png":
        return "mdi-file-image";
      case "application/pdf":
        return "mdi-file-pdf";
      case "application/msword":
        return "mdi-file-word";
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        return "mdi-file-word";
      case "application/vnd.ms-excel":
      case "text/csv":
        return "mdi-file-excel";
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        return "mdi-file-excel";
      default:
        return "mdi-file";
    }
  };
  const formatStringDate = (dateString, format) => {
    let date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", format).format(date);
  };
  const formatDate = (date, format) => {
    return new Intl.DateTimeFormat("en-GB", format).format(date);
  };
  const parseDate = (dateStr) => {
    return new Date(dateStr);
  };
  const formatRange = (min, max, separator = " - ") => {
    const minDate = parseDate(min);
    const maxDate = parseDate(max);
    const currentDate = /* @__PURE__ */ new Date();
    if (maxDate < currentDate) {
      if (maxDate.getFullYear() === currentDate.getFullYear()) {
        return "Ended " + formatDate(maxDate, { day: "numeric", month: "short" });
      } else {
        return "Ended " + formatDate(maxDate, { day: "numeric", month: "short", year: "2-digit" });
      }
    } else if (minDate >= currentDate) {
      if (minDate.getFullYear() === currentDate.getFullYear()) {
        return "Starts " + formatDate(minDate, { day: "numeric", month: "short" });
      } else {
        return "Starts " + formatDate(minDate, { day: "numeric", month: "short", year: "2-digit" });
      }
    } else if (minDate < currentDate && maxDate >= currentDate) {
      if (maxDate.getFullYear() === currentDate.getFullYear()) {
        return "Ends " + formatDate(maxDate, { day: "numeric", month: "short" });
      } else {
        return "Ends " + formatDate(maxDate, { day: "numeric", month: "short", year: "2-digit" });
      }
    } else {
      if (minDate.getFullYear() === maxDate.getFullYear()) {
        return formatDate(minDate, { day: "numeric", month: "short" }) + separator + formatDate(maxDate, {
          day: "numeric",
          month: "short"
        });
      } else {
        return formatDate(minDate, {
          day: "numeric",
          month: "short",
          year: "2-digit"
        }) + separator + formatDate(maxDate, { day: "numeric", month: "short", year: "2-digit" });
      }
    }
  };
  const filtersToBase64String = (filters) => {
    return btoa(JSON.stringify(filters));
  };
  const base64StringToObj = (base64String) => {
    let base64 = atob(base64String);
    try {
      return JSON.parse(base64);
    } catch (e) {
      return null;
    }
  };
  function outputDateFromDb(dateStr, formatOut = "dd/MM/yyyy") {
    const utcDateTime = DateTime.fromISO(dateStr, { zone: "utc" });
    const localDateTime = utcDateTime.setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    return localDateTime.toFormat(formatOut);
  }
  function localDateForDb(date) {
    return DateTime.fromJSDate(date).toUTC().toISO();
  }
  function inputDateForDb(dateStr, formatIn = "dd/MM/yyyy") {
    const localDateTime = DateTime.fromFormat(dateStr, formatIn, {
      zone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    const utcDateTime = localDateTime.toUTC();
    return utcDateTime.toISO();
  }
  const cloneDeep = (obj) => {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (e) {
      return null;
    }
  };
  const extractFilterValues = (filters) => {
    let values = {};
    filters.forEach((filter) => {
      values[filter.key] = filter.value;
    });
    return values;
  };
  const uploadFilenameToUrl = (fileLocation, size) => {
    const atomNuxtConfig = useRuntimeConfig().public.atomNuxt;
    let prefix = size === "thumbnail" ? "thumbnail_" : size === "medium" ? "medium_" : size === "original" ? "" : "large_";
    return `${atomNuxtConfig.storageBaseUrl}/${prefix}${fileLocation}`;
  };
  const uploadToUrl = (upload, size) => {
    const atomNuxtConfig = useRuntimeConfig().public.atomNuxt;
    if (!upload) return "";
    var prefix = size === "thumbnail" ? "thumbnail_" : size === "medium" ? "medium_" : size === "original" ? "" : "large_";
    return `${atomNuxtConfig.storageBaseUrl}/${prefix}${upload.fileLocation}`;
  };
  const getFileType = (file) => {
    switch (file.type) {
      case "image/jpg":
      case "image/jpeg":
      case "image/png":
        return 1;
      case "application/pdf":
        return 2;
      case "application/msword":
        return 3;
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        return 4;
      case "application/vnd.ms-excel":
        return 5;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        return 6;
    }
    return null;
  };
  const stringifyQuery = (params, prefix = "", options = { arrayFormat: "index", skipNull: true, skipEmptyString: true }) => {
    return `${prefix}${queryString.stringify(params, options)}`;
  };
  const parseQuery = (query) => {
    return queryString.parse(query, { arrayFormat: "index", parseNumbers: true });
  };
  return {
    localDateForDb,
    cloneDeep,
    flattenQuery,
    parseQuery,
    stringifyQuery,
    getFileType,
    mimeTypeToMdiIcon,
    toBase64,
    convertAddress,
    formatRange,
    formatDate,
    formatStringDate,
    parseDate,
    filtersToBase64String,
    base64StringToObj,
    extractFilterValues,
    uploadToUrl,
    uploadFilenameToUrl,
    outputDateFromDb,
    inputDateForDb
  };
}
